import { useMemo, useState } from 'react';
import { useConfirmationModal } from '@customerio/pluma-components/react';
import { useZodForm } from '@/components/form';
import { useNavigationGuard } from '@/services/navigation-guard';
import { useHistoryLeaveBlocker } from '@/services/navigation-guard/use-history-leave-blocker';
import type { Integration, IntegrationSetting } from '@/services/db/integration/entity';
import type { DataOutIntegration } from '@/services/db/data-out-integration/entity';
import {
  useCreateDataOutIntegration,
  useDeleteDataOutIntegration,
  useUpdateDataOutIntegration,
} from '@/services/db/data-out-integration/mutations';
import { defaultDataOutSettings } from '@/services/db/data-out-integration/utils';
import { buildDataOutFormSchema, settingsForSave, type DataOutFormValues, type DataOutSettingValue } from './schema';

type Snackbars = { create: (options: { variant: 'default' | 'error'; id: string; message: string }) => unknown };

interface UseDataOutConnectorFormOptions {
  integration: Integration;
  dataOut: DataOutIntegration | null;
  settings: readonly IntegrationSetting[];
  snackbars: Snackbars;
  navigate: (href: string) => void;
  overviewHref: string;
  canWrite: boolean;
}

/**
 * Owns the connector form's state, its two save paths, and disconnect.
 *
 * `resolution` is carried through rather than edited: the form has never exposed it, but the update
 * endpoint requires it, so it round-trips from the loaded record (and defaults to the model's `true`
 * on first connect) instead of being reset to a backend default on every save.
 *
 * `canSave` folds in the write permission and the in-flight states deliberately, so the Save button
 * and the submit handler's guard read the same boolean. Splitting them is what lets an Enter-key
 * submission slip past a disabled button.
 */
export function useDataOutConnectorForm({
  integration,
  dataOut,
  settings,
  snackbars,
  navigate,
  overviewHref,
  canWrite,
}: UseDataOutConnectorFormOptions) {
  const isConnected = dataOut != null;
  const confirm = useConfirmationModal();
  const [isRemoving, setIsRemoving] = useState(false);

  const schema = useMemo(() => buildDataOutFormSchema(settings, isConnected), [settings, isConnected]);

  const defaultValues = useMemo<DataOutFormValues>(
    () => ({
      settings:
        dataOut == null
          ? defaultDataOutSettings(settings)
          : // Start from the schema's blanks so a setting the stored record predates still renders a
            // controlled field, then lay the saved values over it. Secrets come back absent and stay
            // blank, which is what the placeholder communicates.
            { ...defaultDataOutSettings(settings), ...toFormSettings(dataOut.settings) },
      events: dataOut?.events ?? [],
      enabled: dataOut?.enabled ?? false,
      with_content: dataOut?.with_content ?? false,
    }),
    [dataOut, settings],
  );

  const form = useZodForm({ schema, defaultValues });

  const createConnector = useCreateDataOutIntegration();
  const updateConnector = useUpdateDataOutIntegration();
  const deleteConnector = useDeleteDataOutIntegration();

  const isSaving = createConnector.isPending || updateConnector.isPending;
  const { isDirty, isValid } = form.formState;
  // `!isRemoving` is stricter than Ember, which only disabled the Remove button while a removal ran
  // and left Save live — so a dirty form could PUT a record the DELETE was already tearing down.
  const canSave = isDirty && isValid && !isSaving && !isRemoving && canWrite;

  const save = async (values: DataOutFormValues) => {
    const write = {
      settings: settingsForSave(settings, values.settings, isConnected),
      events: values.events,
      enabled: values.enabled,
      with_content: values.with_content,
      resolution: dataOut?.resolution ?? true,
    };

    if (isConnected) {
      await updateConnector.mutateAsync({ identifier: integration.id, ...write });
    } else {
      await createConnector.mutateAsync({ identifier: integration.id, ...write });
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    // Enter-key submission bypasses a disabled button, so the guard has to repeat every condition
    // the button gates on — which is why they share one `canSave` rather than each computing its own.
    if (!canSave) {
      return;
    }

    try {
      await save(values);
    } catch {
      snackbars.create({
        variant: 'error',
        id: 'data-out-type-create.error',
        message: `Something went wrong when we tried to ${
          isConnected ? 'update' : 'create'
        } ${integration.name} integration. Please try again.`,
      });
      // Ember navigated away here regardless, because its save task swallowed the failure before
      // the redirect ran — which discarded the user's edits on a failed save. Staying put is the
      // deliberate fix.
      return;
    }

    // Re-baseline before navigating, or the leave guard still sees a dirty form.
    form.reset(form.getValues(), { keepDefaultValues: false });
    snackbars.create({
      variant: 'default',
      id: 'data-out-type-create.success',
      message: `${integration.name} has been ${isConnected ? 'updated' : 'added'}.`,
    });
    navigate(overviewHref);
  });

  const onRemove = async () => {
    const confirmed = await confirm({
      title: 'Remove integration',
      message: 'Are you sure you want to disable this integration and clear all configurations?',
      confirmLabel: 'Remove integration',
      cancelLabel: 'Cancel',
    });

    if (confirmed !== true) {
      return;
    }

    setIsRemoving(true);
    try {
      await deleteConnector.mutateAsync({ identifier: integration.id });
      snackbars.create({
        variant: 'default',
        id: 'data-out-type-delete.success',
        message: `Removed ${integration.name} integration`,
      });
      // The record's cache slot is now `null`, so the page re-renders as not-connected — the
      // equivalent of Ember's `reloadModel` refresh.
      form.reset({ settings: defaultDataOutSettings(settings), events: [], enabled: false, with_content: false });
    } catch {
      snackbars.create({
        variant: 'error',
        id: 'data-out-type-delete.error',
        message: `Could not remove ${integration.name} integration. Please try again.`,
      });
    } finally {
      setIsRemoving(false);
    }
  };

  // A getter, not a boolean: a navigation queued in the same tick as a save must see the
  // re-baselined state, which a value captured at render time would miss.
  const guard = { hasUnsavedChanges: () => form.formState.isDirty && !isSaving };
  useNavigationGuard(guard);
  useHistoryLeaveBlocker(guard);

  return { form, canSave, isSaving, isRemoving, onSubmit, onRemove };
}

/** The API types settings as `unknown`; the form only ever renders strings and booleans. */
function toFormSettings(settings: Record<string, unknown>): Record<string, DataOutSettingValue> {
  const values: Record<string, DataOutSettingValue> = {};
  for (const [key, value] of Object.entries(settings)) {
    if (typeof value === 'boolean' || typeof value === 'string') {
      values[key] = value;
    } else if (typeof value === 'number') {
      values[key] = String(value);
    }
  }
  return values;
}
